import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  type SxProps,
  type Theme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import BlockIcon from "@mui/icons-material/Block";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MessageIcon from "@mui/icons-material/Message";
import CloseIcon from "@mui/icons-material/Close";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { ReactNode, useEffect, useMemo, useId, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Pages } from "../../utils/route-paths";
import { normalizeDesignerAliasId } from "../../utils/designer-alias";
import { getMatchScoreColors, getMatchScoreValue } from "../../pages/matches-page/utils";
import EducationIcon from "../../assets/designer-profile/Education Degree or Barcherlors.svg";
import CertificationIcon from "../../assets/designer-profile/Certified Interior Designer.svg";

// Import the purple icons
import BookIcon from "../../assets/designer-profile/Book Icon.svg";
import CurrentlyReadingIcon from "../../assets/designer-profile/Currently Reading.svg";
import MusicGenreIcon from "../../assets/designer-profile/Music Genre.svg";
import FavoriteMovieIcon from "../../assets/designer-profile/Favorite Mobe or Show.svg";

// Import location icons
import LocationPinIcon from "../../assets/designer-profile/Location Pin.svg";
import MapIcon from "../../assets/designer-profile/Map Icon.svg";

export type ExperienceStat = {
  label: string;
  value?: string | number | null;
};

export type ProjectGalleryImage = {
  id: string;
  url?: string | null;
};

export type FavoriteProject = {
  id: string;
  title: string;
  imageUrl?: string | null;
  description?: string | null;
  href?: string | null;
  projectTypeLabel?: string | null;
  projectStyleLabel?: string | null;
  specialRequirements?: string[];
  budgetRangeLabel?: string | null;
  projectDurationLabel?: string | null;
  squareFootageLabel?: string | null;
  gallery?: ProjectGalleryImage[];
};

export type FavoriteItem = {
  label: string;
  value?: string | null;
  icon?: ReactNode;
};

export type Testimonial = {
  id: string;
  text: string;
  author?: string | null;
  location?: string | null;
  avatarUrl?: string | null;
  rating?: number | null;
};

export type CompatibilitySummary = {
  percentage?: number | null;
  label?: string | null;
  description?: string | null;
  ratingText?: string | null;
  ratingValue?: number | null;
  ratingScale?: number | null;
};

export type DesignerPremiumProfileProps = {
  designerName: string;
  designerId?: string | null;
  aliasId?: string | null;
  locationLabel?: string | null;
  distanceLabel?: string | null;
  avatarUrl?: string | null;
  coverImageUrl?: string | null;
  coverImagePositionX?: number;
  coverImagePositionY?: number;
  availabilityStatusLabel?: string | null;
  isOnline?: boolean | null;
  compatibility?: CompatibilitySummary | null;
  topAttributes?: string[];
  experienceStats?: ExperienceStat[];
  aboutMe?: string | null;
  certifications?: string[] | null;
  favoriteProjects?: FavoriteProject[];
  personalNote?: string | null;
  designAdvice?: string | null;
  favorites?: FavoriteItem[];
  testimonials?: Testimonial[];
  onContactDesigner?: () => void;
  onCompatibilityLearnMore?: () => void;
  mobileContactAction?: ReactNode;
  onLove?: () => void;
  onLike?: () => void;
  onDismiss?: () => void;
  onBack?: () => void;
  isFavorite?: boolean;
  isLoved?: boolean;
  isLiked?: boolean;
  isDismissed?: boolean;
  enableActions?: boolean;
  ctaLabel?: string;
  secondaryCtaLabel?: string;
  // New props for alias system
  isViewingAsDesigner?: boolean; // true if designer viewing their own profile
  isViewingAsHomeowner?: boolean; // true if homeowner viewing designer profile
  isContactDisabled?: boolean;
  contactDisabledReason?: string | null;
};

const SECTION_CARD_BASE_SX: SxProps<Theme> = {
  backgroundColor: "#fff",
  borderRadius: 3, // 12px
  px: { xs: 2, md: 3 },
  py: { xs: 2, md: 3 },
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)", // Subtle, soft shadow
  height: "100%",
};

const SECTION_TITLE_SX: SxProps<Theme> = {
  color: "#000",
  fontFamily: "var(--Font-Family-Body, Jost)",
  fontSize: "var(--Font-Size-LG, 18px)",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "var(--Font-Line-Height-MD, 32px)",
};

const SectionCard = ({
  children,
  title,
  action,
  sx,
}: {
  children: ReactNode;
  title?: string;
  action?: ReactNode;
  sx?: SxProps<Theme>;
}) => {
  const mergedSx: SxProps<Theme> = useMemo(() => {
    if (!sx) {
      return SECTION_CARD_BASE_SX;
    }
    if (Array.isArray(sx)) {
      return [SECTION_CARD_BASE_SX, ...sx];
    }
    return [SECTION_CARD_BASE_SX, sx];
  }, [sx]);

  return (
    <Box sx={mergedSx}>
      {(title || action) && (
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent={{ xs: "flex-start", md: "space-between" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          mb={title ? 2 : 0}
        >
          {title && (
            <Typography variant="h6" sx={SECTION_TITLE_SX}>
              {title}
            </Typography>
          )}
          {action}
        </Stack>
      )}
      {children}
    </Box>
  );
};

const formatStatValue = (value?: string | number | null) => {
  if (value === undefined || value === null) {
    return "—";
  }
  if (typeof value === "number") {
    return value.toLocaleString();
  }
  if (value.trim() === "") {
    return "—";
  }
  return value;
};

const COMPATIBILITY_ICON_PATH =
  "M27.1576 19.5447C33.2056 18.3914 36.9184 15.0881 37.7955 10H26.6141C25.8823 11.6595 24.2734 12.5519 21.9274 12.5519C19.5813 12.5519 18.08 11.6595 17.3698 10H6.20447C7.07079 15.0881 10.7621 18.3966 16.7994 19.5447C9.83656 20.8702 6 25.0711 6 31.5473C6 32.4031 6.06457 33.2224 6.20447 34H16.7779V27.983L21.852 25.4677L27.0768 27.983V31.4429C27.0768 31.4795 27.0768 31.5108 27.0768 31.5473C27.0768 31.5786 27.0768 31.6047 27.0768 31.636V34H37.7955C37.93 33.2224 38 32.4031 38 31.5473C38 25.0711 34.1312 20.8702 27.1576 19.5447Z";
const COMPATIBILITY_ICON_VIEWBOX = "6 10 32 24";
const COMPATIBILITY_ICON_OUTLINE_COLOR = "#C9C4D3";

const HERO_HEIGHT_XS = 220;
const HERO_HEIGHT_MD = 220;
const AVATAR_SIZE = 120;
const AVATAR_OVERLAP = AVATAR_SIZE / 2;

const CompatibilityGlyphIcon = ({
  variant,
  width = 28,
  height,
}: {
  variant: "full" | "half" | "empty";
  width?: number;
  height?: number;
}) => {
  const resolvedHeight = height ?? width;
  const uniqueId = useId().replace(/:/g, "");
  const gradientId = `compatGlyphGradient-${uniqueId}`;
  const maskId = `compatGlyphMask-${uniqueId}`;

  return (
    <svg
      width={width}
      height={resolvedHeight}
      viewBox={COMPATIBILITY_ICON_VIEWBOX}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="0"
          x2="1"
          y2="0"
          gradientUnits="objectBoundingBox"
        >
          <stop offset="0" stopColor="#FFE08A" />
          <stop offset="0.5" stopColor="#FFC445" />
          <stop offset="1" stopColor="#F59E0B" />
        </linearGradient>
        <mask
          id={maskId}
          maskUnits="objectBoundingBox"
          x="0"
          y="0"
          width="1"
          height="1"
        >
          <rect x="0" y="0" width="0.5" height="1" fill="white" />
        </mask>
      </defs>

      {variant === "full" && (
        <path d={COMPATIBILITY_ICON_PATH} fill={`url(#${gradientId})`} />
      )}

      {variant === "half" && (
        <g mask={`url(#${maskId})`}>
          <path d={COMPATIBILITY_ICON_PATH} fill={`url(#${gradientId})`} />
        </g>
      )}

      <path
        d={COMPATIBILITY_ICON_PATH}
        fill="none"
        stroke={COMPATIBILITY_ICON_OUTLINE_COLOR}
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const buildRatingGlyphVariants = (
  rating: number,
  scale: number
): Array<"full" | "half" | "empty"> => {
  const clampedRating = Math.max(0, Math.min(rating, scale));
  let fullCount = Math.floor(clampedRating);
  const fraction = clampedRating - fullCount;
  let hasHalf = false;

  if (fraction >= 0.75) {
    fullCount = Math.min(fullCount + 1, scale);
  } else if (fraction >= 0.25) {
    hasHalf = true;
  }

  const variants: Array<"full" | "half" | "empty"> = [];

  for (let i = 0; i < scale; i += 1) {
    if (i < fullCount) {
      variants.push("full");
    } else if (hasHalf && i === fullCount) {
      variants.push("half");
      hasHalf = false;
    } else {
      variants.push("empty");
    }
  }

  return variants;
};

export const DesignerPremiumProfile = ({
  designerName,
  designerId,
  aliasId,
  locationLabel,
  distanceLabel,
  avatarUrl,
  coverImageUrl,
  coverImagePositionX = 0,
  coverImagePositionY = 0,
  isOnline = null,
  compatibility,
  topAttributes,
  experienceStats,
  aboutMe,
  certifications,
  favoriteProjects,
  personalNote,
  designAdvice,
  favorites,
  testimonials,
  onContactDesigner,
  onCompatibilityLearnMore,
  mobileContactAction,
  onLove,
  onLike,
  onDismiss,
  onBack,
  isFavorite,
  isLoved,
  isLiked,
  isDismissed,
  enableActions = true,
  ctaLabel = "Connect With Designer",
  secondaryCtaLabel,
  isViewingAsDesigner = false,
  isViewingAsHomeowner = false,
  isContactDisabled = false,
  contactDisabledReason = null,
}: DesignerPremiumProfileProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 900px)");
  const backToPath = `${location.pathname}${location.search}`;
  const hasFavoriteProjects = favoriteProjects && favoriteProjects.length > 0;
  const loveActive = Boolean(isLoved ?? isFavorite);
  const likeActive = Boolean(isLiked);
  const dismissActive = Boolean(isDismissed);
  const contactDisabledMessage =
    contactDisabledReason || "Messaging is unavailable for this match.";
  const availabilityDotColor =
    isOnline === false ? "#D24B4B" : isOnline === true ? "#0CCE6B" : "#9CA3AF";
  const favoriteProjectCards = useMemo(
    () => (favoriteProjects ?? []).slice(0, Math.min((favoriteProjects ?? []).length, 5)),
    [favoriteProjects]
  );
  const normalizedAliasId = normalizeDesignerAliasId(aliasId);
  const testimonialCards = testimonials ?? [];
  const handleViewAllProjects = () => {
    if (!designerId) return;
    const targetPath = isViewingAsHomeowner
      ? Pages.DESIGNER_FILES_HOMEOWNER
      : Pages.DESIGNER_FILES_PUBLIC;
    const searchParams = new URLSearchParams({ designerId });
    navigate(`${targetPath}?${searchParams.toString()}`, {
      state: { designerId },
    });
  };

  // Alias display logic
  const getDisplayName = () => {
    if (isViewingAsHomeowner) {
      // Homeowner sees alias in large pink text
      if (normalizedAliasId) {
        return normalizedAliasId;
      } else {
        // Show loading or placeholder if no aliasId is available
        return "Designer";
      }
    } else if (isViewingAsDesigner) {
      // Designer sees their real name with alias in small grey text
      return designerName;
    }
    // Default fallback
    return designerName;
  };

  const getSubtitleText = () => {
    if (isViewingAsHomeowner) {
      // Homeowner doesn't see real name
      return null;
    } else if (isViewingAsDesigner) {
      // Designer sees their alias as subtitle
      if (normalizedAliasId) {
        return normalizedAliasId;
      } else {
        // Show loading or placeholder if no aliasId is available
        return "Generating alias...";
      }
    }
    return null;
  };

const COMPATIBILITY_RING_RADIUS = 46;
const COMPATIBILITY_RING_STROKE = 18;
const COMPATIBILITY_RING_CIRCUMFERENCE =
  2 * Math.PI * COMPATIBILITY_RING_RADIUS;

  const ratingScale = Math.max(compatibility?.ratingScale ?? 5, 1);

  const compatibilityPercentage = useMemo(() => {
    if (compatibility?.percentage === undefined || compatibility?.percentage === null) {
      return null;
    }
    const numericValue = Number(compatibility.percentage);
    if (Number.isNaN(numericValue)) {
      return null;
    }
    return Math.max(0, Math.min(100, numericValue));
  }, [compatibility]);

  const DEFAULT_PREVIEW_COMPATIBILITY = 75;
  const DEFAULT_PREVIEW_RATING = 4.6;

  const ringProgress = compatibilityPercentage ?? DEFAULT_PREVIEW_COMPATIBILITY;
  const clampProgress = useMemo(
    () => Math.max(0, Math.min(100, ringProgress)),
    [ringProgress]
  );
  const ringColors = useMemo(
    () => getMatchScoreColors(getMatchScoreValue(clampProgress)),
    [clampProgress]
  );

  const [animatedProgress, setAnimatedProgress] = useState(0);

  useEffect(() => {
    const startValue = animatedProgress;
    const targetValue = clampProgress;
    if (Math.abs(startValue - targetValue) < 0.01) {
      setAnimatedProgress(targetValue);
      return;
    }

    const duration = 1200;
    let animationFrame: number;
    const startTime = performance.now();

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      const nextValue = startValue + (targetValue - startValue) * eased;
      setAnimatedProgress(nextValue);
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [clampProgress]);

  const compatibilityDisplayValue = Math.round(
    Math.max(0, Math.min(100, animatedProgress))
  );

  const experienceLabelOverrides = ["Years Active", "Matches", "HDG Projects"];

  const normalizedRating = useMemo(() => {
    const rawRating =
      compatibility?.ratingValue === undefined ||
      compatibility?.ratingValue === null
        ? DEFAULT_PREVIEW_RATING
        : compatibility.ratingValue;
    const numericValue = Number(rawRating);
    if (Number.isNaN(numericValue)) {
      return Math.min(Math.max(DEFAULT_PREVIEW_RATING, 0), ratingScale);
    }
    return Math.min(Math.max(numericValue, 0), ratingScale);
  }, [compatibility, ratingScale]);

  const experienceStatsDisplay = (experienceStats ?? []).map((stat, index) => ({
    ...stat,
    label: experienceLabelOverrides[index] ?? stat.label,
  }));

  const starVariants = useMemo(
    () => buildRatingGlyphVariants(normalizedRating, ratingScale),
    [normalizedRating, ratingScale]
  );

  return (
    <Box
      className="designer-premium-profile"
      sx={{
        backgroundColor: "#F5F5F5",
        py: { xs: 3, md: 3 },
      }}
    >
      <Box className="site-wrapper designer-premium-profile-wrapper">
        <Stack spacing={{ xs: 3, md: 4 }}>
          {onBack && (
            <Button
              onClick={onBack}
              startIcon={<ArrowBackIcon />}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                backgroundColor: "#fff",
                color: "#111",
                borderRadius: 999,
                px: 2.5,
                py: 1,
                boxShadow: "0px 4px 12px rgba(15, 23, 42, 0.08)",
                border: "1px solid rgba(17, 17, 17, 0.1)",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Back
            </Button>
          )}

          <Box
            className="designer-premium-hero-card"
            sx={{
              position: "relative",
              borderRadius: 3,
              boxShadow: "0px 4px 24px rgba(15, 23, 42, 0.12)",
              backgroundColor: "#fff",
              overflow: "visible",
            }}
          >
            <Box
              className="designer-premium-hero-cover"
              sx={{
                height: { xs: HERO_HEIGHT_XS, md: HERO_HEIGHT_MD },
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
                overflow: "hidden",
                position: "relative",
                backgroundColor: coverImageUrl ? "#111" : "#1F2937",
              }}
            >
              {coverImageUrl ? (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `url(${coverImageUrl})`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundPosition: `calc(50% + ${coverImagePositionX}px) calc(50% + ${coverImagePositionY}px)`,
                    transition: "background-position 0.25s ease-out",
                    willChange: "background-position",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(135deg, #1F2937 0%, #111827 100%)",
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                position: "absolute",
                left: { xs: 24, md: 48 },
                top: {
                  xs: `calc(${HERO_HEIGHT_XS}px - ${AVATAR_OVERLAP}px)`,
                  md: `calc(${HERO_HEIGHT_MD}px - ${AVATAR_OVERLAP}px)`,
                },
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                borderRadius: "50%",
                border: "6px solid #fff",
                boxShadow: "0px 16px 40px rgba(15, 23, 42, 0.16)",
                overflow: "visible",
                backgroundColor: "#fff",
              }}
            >
              <Avatar
                src={avatarUrl ?? undefined}
                alt={designerName}
                sx={{ width: "100%", height: "100%" }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 6,
                  right: 6,
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  border: "4px solid #fff",
                  backgroundColor: availabilityDotColor,
                  transform: "translate(10%, 10%)",
                  zIndex: 2,
                }}
              />
            </Box>

            <Box
              sx={{
                backgroundColor: "#fff",
                px: { xs: 3, md: 4 },
                pt: {
                  xs: `calc(${AVATAR_OVERLAP}px + 24px)`,
                  md: `calc(${AVATAR_OVERLAP}px + 24px)`,
                },
                pb: { xs: 3, md: 3 },
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 3, md: 4 }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
              >
                <Stack spacing={1}>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{
                      color: isViewingAsHomeowner
                        ? "var(--Secondary, #D22268)"
                        : "#1C1C1C",
                      fontFamily: isViewingAsHomeowner
                        ? "var(--Font-Family-Titles, Jost)"
                        : "inherit",
                      fontSize: isViewingAsHomeowner
                        ? "var(--Font-Size-2XL, 25px)"
                        : { xs: "18px", md: "2rem" },
                      fontStyle: isViewingAsHomeowner ? "normal" : "inherit",
                      lineHeight: isViewingAsHomeowner
                        ? "var(--Font-Line-Height-LG, 40px)"
                        : { xs: "24px", md: "normal" },
                    }}
                  >
                    {getDisplayName()}
                  </Typography>
                  {getSubtitleText() && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: "#9E9E9E", // Light grey
                        fontSize: "0.875rem"
                      }}
                    >
                      {getSubtitleText()}
                    </Typography>
                  )}
                  <Stack
                    direction="row"
                    spacing={3}
                    flexWrap="wrap"
                    sx={{ color: "rgba(28, 28, 28, 0.7)" }}
                  >
                    {locationLabel && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box
                          component="img"
                          src={LocationPinIcon}
                          alt="Location"
                          sx={{
                            width: 16,
                            height: 16,
                            filter: 'hue-rotate(300deg) saturate(2) brightness(0.8)', // Convert to pink color
                          }}
                        />
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {locationLabel}
                        </Typography>
                      </Stack>
                    )}
                    {distanceLabel && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box
                          component="img"
                          src={MapIcon}
                          alt="Distance"
                          sx={{
                            width: 16,
                            height: 16,
                            filter: 'hue-rotate(300deg) saturate(2) brightness(0.8)', // Convert to pink color
                          }}
                        />
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {distanceLabel}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>

                {enableActions && !isMobile && (
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "flex-start", md: "center" }}
                  >
                    <Button
                      variant={loveActive ? "contained" : "outlined"}
                      startIcon={<FavoriteIcon />}
                      onClick={onLove}
                      aria-pressed={loveActive}
                      sx={{
                        borderRadius: 999,
                        px: 3,
                        py: 1,
                        borderColor: "#E91E63",
                        color: "#E91E63",
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: "#E91E63",
                          color: "#fff",
                          borderColor: "#E91E63",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#E91E63",
                          color: "#fff",
                          borderColor: "#E91E63",
                        },
                        ...(loveActive && {
                          backgroundColor: "#E91E63",
                          color: "#fff",
                          borderColor: "#E91E63",
                          "&:hover": {
                            backgroundColor: "#C2185B",
                            borderColor: "#C2185B",
                          },
                        }),
                      }}
                    >
                      Love
                    </Button>
                    <Button
                      variant={likeActive ? "contained" : "outlined"}
                      startIcon={<ThumbUpIcon />}
                      onClick={onLike}
                      aria-pressed={likeActive}
                      sx={{
                        borderRadius: 999,
                        px: 3,
                        py: 1,
                        borderColor: "#E91E63",
                        color: "#E91E63",
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: "#E91E63",
                          color: "#fff",
                          borderColor: "#E91E63",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#E91E63",
                          color: "#fff",
                          borderColor: "#E91E63",
                        },
                        ...(likeActive && {
                          backgroundColor: "#E91E63",
                          color: "#fff",
                          borderColor: "#E91E63",
                          "&:hover": {
                            backgroundColor: "#C2185B",
                            borderColor: "#C2185B",
                          },
                        }),
                      }}
                    >
                      Like
                    </Button>
                    <Button
                      variant={dismissActive ? "contained" : "outlined"}
                      startIcon={<BlockIcon />}
                      onClick={onDismiss}
                      aria-pressed={dismissActive}
                      sx={{
                        borderRadius: 999,
                        px: 3,
                        py: 1,
                        borderColor: "#E91E63",
                        color: "#E91E63",
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: "#E91E63",
                          color: "#fff",
                          borderColor: "#E91E63",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#E91E63",
                          color: "#fff",
                          borderColor: "#E91E63",
                        },
                        ...(dismissActive && {
                          backgroundColor: "#E91E63",
                          color: "#fff",
                          borderColor: "#E91E63",
                          "&:hover": {
                            backgroundColor: "#C2185B",
                            borderColor: "#C2185B",
                          },
                        }),
                      }}
                    >
                      Not for Me
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<MessageIcon />}
                      onClick={onContactDesigner}
                      disabled={isContactDisabled}
                      title={isContactDisabled ? contactDisabledMessage : undefined}
                      sx={{
                        borderRadius: 999,
                        px: 3,
                        py: 1,
                        borderColor: "#4f46e5",
                        color: "#4f46e5",
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: "#4f46e5",
                          color: "#fff",
                          borderColor: "#4f46e5",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#4f46e5",
                          color: "#fff",
                          borderColor: "#4f46e5",
                        },
                      }}
                    >
                      Contact Designer
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Box>

          {enableActions && isMobile && (
            <Box className="designer-premium-mobile-actions" role="group" aria-label="Match actions">
              <button
                type="button"
                className={`designer-premium-action-btn is-dismiss ${
                  dismissActive ? "is-active" : ""
                }`}
                aria-label="Not for me"
                onClick={onDismiss ?? undefined}
              >
                <CloseIcon />
              </button>
              <button
                type="button"
                className={`designer-premium-action-btn is-like ${
                  likeActive ? "is-active" : ""
                }`}
                aria-label="Like"
                onClick={onLike ?? undefined}
              >
                <StarBorderIcon />
              </button>
              <button
                type="button"
                className={`designer-premium-action-btn is-love ${
                  loveActive ? "is-active" : ""
                }`}
                aria-label="Love"
                onClick={onLove ?? undefined}
              >
                <FavoriteIcon />
              </button>
              {mobileContactAction ?? (
                <button
                  type="button"
                  className="designer-premium-action-btn is-contact"
                  aria-label="Contact"
                  disabled={isContactDisabled}
                  title={isContactDisabled ? contactDisabledMessage : undefined}
                  onClick={onContactDesigner ?? undefined}
                >
                  <ChatBubbleOutlineIcon />
                </button>
              )}
            </Box>
          )}

          <Stack spacing={{ xs: 1.25, md: 1.25 }} className="designer-premium-sections">
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              alignItems="stretch"
              sx={{ width: "100%" }}
            >
              <Box sx={{ flexShrink: 0 }}>
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: 3,
                    px: 2.5,
                    py: 2.5,
                    boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
                    display: "flex",
                    flexDirection: { xs: "row", md: "row" },
                    alignItems: { xs: "center", md: "stretch" },
                    gap: 2,
                    minWidth: { xs: "100%", md: 320 },
                    width: { xs: "100%", md: "fit-content" },
                  }}
                >
                  <Stack
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ textAlign: "center", minWidth: 140 }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: { xs: 88, md: 120 },
                        height: { xs: 88, md: 120 },
                      }}
                    >
                      <svg
                        width={isMobile ? 88 : 120}
                        height={isMobile ? 88 : 120}
                        viewBox="0 0 120 120"
                      >
                        <defs>
                          <linearGradient
                            id="compatibilityRingGradient"
                            x1="60"
                            x2="60"
                            y1="0"
                            y2="120"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop offset="0%" stopColor={ringColors.start} />
                            <stop offset="100%" stopColor={ringColors.end} />
                          </linearGradient>
                        </defs>
                        <circle
                          cx="60"
                          cy="60"
                          r={COMPATIBILITY_RING_RADIUS}
                          fill="none"
                          stroke="#E5E7EB"
                          strokeWidth={COMPATIBILITY_RING_STROKE}
                        />
                        <circle
                          cx="60"
                          cy="60"
                          r={COMPATIBILITY_RING_RADIUS}
                          fill="none"
                          stroke="url(#compatibilityRingGradient)"
                          strokeWidth={COMPATIBILITY_RING_STROKE}
                          strokeLinecap="round"
                          strokeDasharray={`${COMPATIBILITY_RING_CIRCUMFERENCE} ${COMPATIBILITY_RING_CIRCUMFERENCE}`}
                          strokeDashoffset={`${
                            COMPATIBILITY_RING_CIRCUMFERENCE -
                            (COMPATIBILITY_RING_CIRCUMFERENCE * animatedProgress) / 100
                          }`}
                          transform="rotate(-90 60 60)"
                        />
                      </svg>
                      <Box
                        sx={{
                          position: "absolute",
                          inset: { xs: 14, md: 20 },
                          borderRadius: "50%",
                          backgroundColor: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "inset 0px 4px 10px rgba(15, 23, 42, 0.06)",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#1C1C1C",
                            fontFamily: "'Jost', sans-serif",
                            fontWeight: 800,
                          fontSize: { xs: "18px", md: "22.98px" },
                            lineHeight: 1,
                          }}
                        >
                          {compatibilityDisplayValue !== null
                            ? `${compatibilityDisplayValue}%`
                            : "—"}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(28, 28, 28, 0.6)",
                        maxWidth: 160,
                        fontSize: "12px",
                        lineHeight: "16px",
                      }}
                    >
                      Smart-Guid Matching
                      <br />
                      Overall Rate
                    </Typography>
                  </Stack>

                  <Stack spacing={1} alignItems="flex-start" sx={{ flex: 1 }}>
                    <Stack spacing={0.5}>
                      <Typography
                        variant="h6"
                        sx={SECTION_TITLE_SX}
                      >
                        {compatibility?.label ?? "Compatibility Rating"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(28, 28, 28, 0.6)" }}
                      >
                        {compatibility?.ratingText ?? "by ColorWorks Group"}
                      </Typography>
                    </Stack>

                    <Stack spacing={0.5} alignItems="flex-start" sx={{ width: "100%" }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ color: "#938F96" }}
                      >
                        {starVariants.map((variant, index) => (
                          <CompatibilityGlyphIcon
                            key={`compat-rating-${variant}-${index}`}
                            variant={variant}
                            width={20}
                            height={16}
                          />
                        ))}
                        <Typography variant="caption" sx={{ color: "#938F96" }}>
                          ({normalizedRating.toFixed(1)})
                        </Typography>
                      </Stack>

                      <Typography variant="caption" sx={{ color: "#938F96" }}>
                        {compatibility?.description ?? "Compatibility Rating"}
                      </Typography>
                    </Stack>

                    <Button
                      onClick={onCompatibilityLearnMore ?? onContactDesigner}
                      sx={{
                        color: "var(--Dark-Purple, #4F46E5)",
                        fontFamily: "var(--Font-Family-Body, Jost)",
                        fontSize: "var(--Font-Size-XL, 20px)",
                        fontStyle: "normal",
                        fontWeight: 600,
                        lineHeight: "normal",
                        textDecorationLine: "underline",
                        textDecorationStyle: "solid",
                        textDecorationSkipInk: "auto",
                        textDecorationThickness: "auto",
                        textUnderlineOffset: "auto",
                        textUnderlinePosition: "from-font",
                        textTransform: "capitalize",
                        px: 0,
                        height: 48,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        mt: 1,
                        "&:hover": {
                          backgroundColor: "transparent",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Learn More
                    </Button>
                  </Stack>
                </Box>
              </Box>
              <Box
                sx={{
                  flexShrink: 0,
                  width: { xs: "100%", md: "fit-content" },
                }}
              >
                <SectionCard
                  title="Top Matching Attributes"
                  sx={{
                    width: { xs: "100%", md: "fit-content" },
                    minWidth: { xs: "100%", md: 312 },
                  }}
                >
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    rowGap={0.5}
                    columnGap={0.5}
                    sx={{ maxWidth: { xs: "100%", md: 312 } }}
                  >
                    {(topAttributes ?? []).slice(0, 6).map((attribute) => (
                      <Chip
                        key={attribute}
                        label={attribute}
                        sx={{
                          display: "flex",
                          height: "28px",
                          padding: "4px 8px",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "6px",
                          borderRadius: "32px",
                          backgroundColor: "var(--Dark-Purple, #4F46E5)",
                          color: "#fff",
                          fontWeight: 600,
                        }}
                      />
                    ))}
                    {(!topAttributes || topAttributes.length === 0) && (
                      <Typography variant="body2" color="text.secondary">
                        No attributes available.
                      </Typography>
                    )}
                  </Stack>
                </SectionCard>
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: { xs: "100%", md: 360 } }}>
              <SectionCard title="Experience" sx={{ height: "100%" }}>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="nowrap"
                  sx={{ overflowX: "auto", width: "100%", minHeight: 120 }}
                  divider={
                    <Divider
                      flexItem
                      orientation="vertical"
                      sx={{
                        borderColor: "#E5E7EB",
                        borderRightWidth: 1,
                        display: { xs: "none", md: "block" },
                      }}
                    />
                  }
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {experienceStatsDisplay.map((stat) => (
                    <Stack
                      key={stat.label}
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        flex: "1 1 30%",
                        minWidth: { xs: 100, md: 0 },
                        maxWidth: 160,
                      }}
                    >
                      <Typography
                        variant="h4"
                        fontWeight={700}
                        sx={{ color: "#5C3BFF" }}
                      >
                        {formatStatValue(stat.value)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "var(--M3-ref-neutral-neutral40, #605D64)",
                          textAlign: "center",
                          fontFeatureSettings: "'ss01' on, 'cv01' on, 'cv11' on",
                          fontFamily: "var(--Font-Family-Body, Jost)",
                          fontSize: "14px",
                          fontStyle: "normal",
                          fontWeight: 400,
                          lineHeight: "var(--Font-Line-Height-SM, 24px)",
                          letterSpacing: "0.28px",
                          textTransform: "capitalize",
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </SectionCard>
              </Box>
            </Stack>

            <Grid container spacing={0} sx={{ alignItems: "stretch" }}>
              <Grid item xs={12} md={7} sx={{ pr: { md: 1.5 } }}>
              <SectionCard title="About Me" sx={{ height: "100%" }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    dangerouslySetInnerHTML={{
                      __html: aboutMe?.trim() ? aboutMe : "No biography provided yet.",
                    }}
                  />
                </SectionCard>
              </Grid>

              <Grid
                item
                xs={12}
                md={5}
                sx={{ pl: { md: 1.5 }, mt: { xs: 1.25, md: 0 } }}
              >
                <SectionCard title="Certifications & Education" sx={{ height: "100%" }}>
                  <Stack spacing={1.5}>
                    {(certifications ?? []).length > 0 ? (
                      certifications?.map((item, index) => {
                        const iconSrc = index === 0 ? CertificationIcon : EducationIcon;
                        return (
                          <Stack
                            key={`${item}-${index}`}
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <Box
                              component="img"
                              src={iconSrc}
                              alt="Certification icon"
                              sx={{ width: 24, height: 24 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {item}
                            </Typography>
                          </Stack>
                        );
                      })
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No certifications listed yet.
                      </Typography>
                    )}
                  </Stack>
                </SectionCard>
              </Grid>
            </Grid>

                <SectionCard
                  title="A Few of My Favorite Projects"
                  action={
                    hasFavoriteProjects ? (
                      <Button
                        variant="text"
                        onClick={handleViewAllProjects}
                        disabled={!designerId}
                        sx={{
                          textTransform: "none",
                          color: "#5C3BFF",
                          fontWeight: 600,
                          "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    View All Projects
                  </Button>
                ) : null
              }
            >
            {hasFavoriteProjects ? (
              <Box
                sx={{
                  display: "grid",
                  gap: { xs: 2, md: 3 },
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(5, minmax(0, 1fr))",
                  },
                }}
              >
                {favoriteProjectCards.map((project) => (
                  <Box
                    key={project.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (!designerId) return;
                      navigate(
                        Pages.DESIGNER_PROJECT_DETAILS.replace(
                          ":designerId",
                          designerId
                        ).replace(":projectId", project.id),
                        {
                          state: {
                            backTo: backToPath,
                            designerName: getDisplayName(),
                          },
                        }
                      );
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (!designerId) return;
                        navigate(
                          Pages.DESIGNER_PROJECT_DETAILS.replace(
                            ":designerId",
                            designerId
                          ).replace(":projectId", project.id),
                          {
                            state: {
                              backTo: backToPath,
                              designerName: getDisplayName(),
                            },
                          }
                        );
                      }
                    }}
                    sx={{
                      position: "relative",
                      borderRadius: 3,
                      overflow: "hidden",
                      aspectRatio: "1 / 1",
                      cursor: "pointer",
                      backgroundColor: "#F3F4F6",
                      outline: "none",
                      transition: "transform 0.3s ease",
                      "&:hover .project-card__image": {
                        transform: { md: "scale(1.05)" },
                      },
                      "&:hover .project-card__overlay": {
                        opacity: { md: 1 },
                      },
                    }}
                  >
                    <Box
                      className="project-card__image"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: project.imageUrl
                          ? `url(${project.imageUrl})`
                          : undefined,
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        transition: "transform 0.4s ease",
                      }}
                    />
                    <Box
                      className="project-card__overlay"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        p: { xs: 2, md: 3 },
                        color: "#FFFFFF",
                        background: {
                          xs: "linear-gradient(0deg, rgba(28, 28, 28, 0.84) 0%, rgba(28, 28, 28, 0.84) 100%)",
                          md: "linear-gradient(0deg, rgba(28, 28, 28, 0.84) 0%, rgba(28, 28, 28, 0.84) 100%)",
                        },
                        backgroundBlendMode: "multiply, normal",
                        opacity: { xs: 1, md: 0 },
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Cormorant Garamond",
                          fontSize: { xs: "2rem", md: "2.5rem" },
                          lineHeight: 1.2,
                          fontWeight: 400,
                          mb: { xs: 0.5, md: 1 },
                          color: "#FFFFFF",
                          textAlign: "center",
                          display: "-webkit-box",
                          WebkitLineClamp: { xs: 1, md: 2 },
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {project.title}
                      </Typography>
                      {project.projectTypeLabel && (
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: "#FFFFFF",
                            display: { xs: "none", md: "block" },
                            textAlign: "center",
                          }}
                        >
                          {project.projectTypeLabel}
                        </Typography>
                      )}
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          mt: { xs: 1, md: 1.5 },
                          color: "#FFFFFF",
                          fontWeight: 600,
                          textTransform: "none",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                        >
                          See Project Details
                        </Typography>
                        <ArrowForwardIcon sx={{ fontSize: "14px" }} />
                      </Stack>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Featured projects will appear here once added to your bio.
              </Typography>
            )}
          </SectionCard>

          <SectionCard title="On a Personal Note">
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Stack spacing={2}>
                  {personalNote && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
                        Why Work With Me
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="div"
                        sx={{
                          "& p": { margin: "0 0 12px" },
                          "& p:last-of-type": { marginBottom: 0 },
                          "& ul, & ol": { paddingLeft: "18px", margin: "0 0 12px" },
                          "& li": { marginBottom: "6px" },
                        }}
                        dangerouslySetInnerHTML={{ __html: personalNote }}
                      />
                    </Box>
                  )}
                  {designAdvice && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
                        My Best Design Advice
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="div"
                        sx={{
                          "& p": { margin: "0 0 12px" },
                          "& p:last-of-type": { marginBottom: 0 },
                          "& ul, & ol": { paddingLeft: "18px", margin: "0 0 12px" },
                          "& li": { marginBottom: "6px" },
                        }}
                        dangerouslySetInnerHTML={{ __html: designAdvice }}
                      />
                    </Box>
                  )}
                  {!personalNote && !designAdvice && (
                    <Typography variant="body2" color="text.secondary">
                      Personal insights will appear here once shared in the bio.
                    </Typography>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} md={5}>
                <Grid container spacing={2}>
                  {(favorites ?? []).map((item) => {
                    // Map labels to specific icons
                    const getIconForLabel = (label: string) => {
                      const lowerLabel = label.toLowerCase();
                      if (lowerLabel.includes('book') && !lowerLabel.includes('reading')) {
                        return <img src={BookIcon} alt="Book" style={{ width: 24, height: 24 }} />;
                      } else if (lowerLabel.includes('reading')) {
                        return <img src={CurrentlyReadingIcon} alt="Currently Reading" style={{ width: 24, height: 24 }} />;
                      } else if (lowerLabel.includes('music')) {
                        return <img src={MusicGenreIcon} alt="Music" style={{ width: 24, height: 24 }} />;
                      } else if (lowerLabel.includes('movie') || lowerLabel.includes('show')) {
                        return <img src={FavoriteMovieIcon} alt="Movie/Show" style={{ width: 24, height: 24 }} />;
                      }
                      return item.icon;
                    };

                    return (
                      <Grid key={item.label} item xs={12} sm={6}>
                        <Card
                          elevation={0}
                          sx={{
                            borderRadius: 3,
                            border: "1px solid #E5E7EB",
                            height: "100%",
                          }}
                        >
                          <CardContent>
                            <Stack spacing={1}>
                              <Box sx={{ color: "#5C3BFF" }}>
                                {getIconForLabel(item.label)}
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ letterSpacing: 0.5, textTransform: "uppercase" }}
                              >
                                {item.label}
                              </Typography>
                              <Typography variant="body2">
                                {item.value && item.value.trim() !== ""
                                  ? item.value
                                  : "Not specified"}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard title="What My Clients Are Saying">
            {testimonialCards.length > 0 ? (
              <Grid container spacing={3}>
                {testimonialCards.map((testimonial) => (
                  <Grid key={testimonial.id} item xs={12} md={4}>
                    <Card
                      elevation={0}
                      sx={{
                        border: "1px solid #E5E7EB",
                        borderRadius: 3,
                        height: "100%",
                      }}
                    >
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {Array.from({ length: 5 }, (_, index) => {
                              const rating = testimonial.rating ?? 0;
                              const isFilled = index < Math.floor(rating);
                              const isHalfFilled = index === Math.floor(rating) && rating % 1 >= 0.5;
                              
                              return (
                                <Box
                                  key={`${testimonial.id}-star-${index}`}
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                                      fill={isFilled || isHalfFilled ? "#FFD700" : "#E5E7EB"}
                                      stroke={isFilled || isHalfFilled ? "#FFD700" : "#D1D5DB"}
                                      strokeWidth="1"
                                    />
                                    {isHalfFilled && (
                                      <path
                                        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                                        fill="#FFD700"
                                        clipPath="inset(0 50% 0 0)"
                                      />
                                    )}
                                  </svg>
                                </Box>
                              );
                            })}
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            “{testimonial.text}”
                          </Typography>
                          <Divider />
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              src={testimonial.avatarUrl ?? undefined}
                              alt={testimonial.author ?? undefined}
                              sx={{ width: 40, height: 40 }}
                            />
                            <Stack spacing={0.25}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {testimonial.author ?? "Valued Client"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {testimonial.location ?? ""}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Testimonials will appear here once clients share feedback.
              </Typography>
            )}
          </SectionCard>

          <SectionCard>
            <Box
              sx={{
                textAlign: "center",
                py: { xs: 4, md: 6 },
                px: { xs: 2, md: 4 },
              }}
            >
              <Stack spacing={2} alignItems="center">
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: "24px", md: "32px" },
                    color: "text.primary",
                  }}
                >
                  Ready to Transform Your Space?
                </Typography>
                <Typography
                  variant="body1"
                    sx={{
                    fontSize: { xs: "16px", md: "18px" },
                    color: "text.secondary",
                    maxWidth: "600px",
                    }}
                  >
                  Let's create something beautiful together.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<MessageIcon />}
                  disabled={isContactDisabled}
                  title={isContactDisabled ? contactDisabledMessage : undefined}
                  sx={{
                    backgroundColor: "#4f46e5",
                    color: "white",
                    px: { xs: 4, md: 5 },
                    py: { xs: 1.5, md: 2 },
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: { xs: "16px", md: "18px" },
                    fontWeight: 600,
                    mt: 2,
                    "&:hover": {
                      backgroundColor: "#4338ca",
                    },
                  }}
                  onClick={onContactDesigner}
                >
                  {ctaLabel}
                </Button>
              </Stack>
            </Box>
          </SectionCard>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
